import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  BEDROOM_OPTIONS, CLASSIFICATIONS, CrmDocument, CrmEvent, CrmLead, CrmLeadListing, CrmTask,
  CLIENT_TYPES, DOC_CATEGORIES, EVENT_TYPES, GARAGE_OPTIONS, GOALS, INTERESTS, LISTING_RELATIONS,
  MARITAL_OPTIONS, PRICE_RANGES, PRIORITIES, REGIONS, SOURCES, STAGES, TASK_TYPES,
  cancelFutureFollowUps, classify, computeScore, createFollowUpSequence, dateTimeBR, emailLink,
  fetchEvents, logEvent, onStageChanged, updateLead, whatsappLink,
} from "@/lib/crm";
import CrmMessageComposer from "./CrmMessageComposer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Check, Clock, Home, Mail, MessageCircle, Plus, Save, Sparkles, Trash2, Upload,
} from "lucide-react";

const db = supabase as any;


interface Props {
  lead: CrmLead;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1">
    <Label className="text-[11px] font-body uppercase tracking-wide text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const Picker = ({
  value,
  options,
  placeholder,
  onChange,
}: {
  value: string;
  options: string[] | { value: string; label: string }[];
  placeholder: string;
  onChange: (v: string) => void;
}) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className="font-body">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        return (
          <SelectItem key={v} value={v}>
            {l}
          </SelectItem>
        );
      })}
    </SelectContent>
  </Select>
);

const MultiCheck = ({
  values,
  options,
  onChange,
}: {
  values: string[];
  options: string[];
  onChange: (v: string[]) => void;
}) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
    {options.map((o) => (
      <label key={o} className="flex items-center gap-2 font-body text-sm cursor-pointer">
        <Checkbox
          checked={values.includes(o)}
          onCheckedChange={(c) => onChange(c ? [...values, o] : values.filter((x) => x !== o))}
        />
        {o}
      </label>
    ))}
  </div>
);

const CrmLeadSheet = ({ lead, open, onClose, onSaved }: Props) => {
  const { toast } = useToast();
  const [form, setForm] = useState<CrmLead>(lead);
  const [events, setEvents] = useState<CrmEvent[]>([]);
  const [tasks, setTasks] = useState<CrmTask[]>([]);
  const [links, setLinks] = useState<CrmLeadListing[]>([]);
  const [docs, setDocs] = useState<CrmDocument[]>([]);
  const [listings, setListings] = useState<{ id: string; title: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);


  const [newEvent, setNewEvent] = useState({ event_type: "nota", title: "", description: "" });
  const [newTask, setNewTask] = useState({
    title: "", due_at: "", priority: "media", task_type: "follow_up", description: "",
  });
  const [newLink, setNewLink] = useState({ listing_id: "", relation: "enviado" });

  useEffect(() => {
    setForm(lead);
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);

  const loadAll = async () => {
    const [ev, tk, lk, dc, ls] = await Promise.all([
      fetchEvents(lead.id),
      db.from("crm_lead_tasks").select("*").eq("lead_id", lead.id).order("due_at"),
      db.from("crm_lead_listings").select("*").eq("lead_id", lead.id).order("created_at", { ascending: false }),
      db.from("crm_lead_documents").select("*").eq("lead_id", lead.id).order("created_at", { ascending: false }),
      db.from("listings").select("id,title").eq("is_active", true).order("title"),
    ]);
    setEvents(ev);
    setTasks((tk.data || []) as CrmTask[]);
    setLinks((lk.data || []) as CrmLeadListing[]);
    setDocs((dc.data || []) as CrmDocument[]);
    setListings((ls.data || []) as { id: string; title: string }[]);
  };

  const set = (patch: Partial<CrmLead>) => setForm({ ...form, ...patch });

  const save = async () => {
    setSaving(true);
    const score = computeScore(form, events);
    const patch: Record<string, unknown> = {
      client_name: form.client_name,
      client_phone: form.client_phone || null,
      client_whatsapp: form.client_whatsapp || null,
      client_email: form.client_email || null,
      cpf: form.cpf || null,
      rg: form.rg || null,
      birth_date: form.birth_date || null,
      marital_status: form.marital_status || null,
      profession: form.profession || null,
      company: form.company || null,
      income: form.income ? Number(form.income) : null,
      city: form.city || null,
      state: form.state || null,
      address: form.address || null,
      source: form.source || null,
      campaign: form.campaign || null,
      ad_group: form.ad_group || null,
      ad_name: form.ad_name || null,
      keyword: form.keyword || null,
      goal: form.goal || null,
      client_type: form.client_type || null,
      interests: form.interests || [],
      price_range: form.price_range || null,
      regions: form.regions || [],
      bedrooms: form.bedrooms || null,
      garage: form.garage || null,
      stage: form.stage,
      broker: form.broker || null,
      deal_value: form.deal_value ? Number(form.deal_value) : null,
      lost_reason: form.lost_reason || null,
      neighborhood: form.neighborhood || null,
      property_name: form.property_name || null,
      general_notes: form.general_notes || null,
      visit_date: form.visit_date || null,
      score,
      classification: classify(score, form.stage),
    };
    const { error } = await updateLead(lead.id, patch);
    setSaving(false);
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    if (form.stage !== lead.stage) {
      await logEvent(lead.id, "status", `Etapa alterada para ${STAGES.find((s) => s.value === form.stage)?.label}`);
      await onStageChanged(lead.id, form.stage);
    }

    toast({ title: "Lead salvo" });
    onSaved();
    void loadAll();
  };

  const addEvent = async () => {
    if (!newEvent.title.trim()) return;
    await logEvent(lead.id, newEvent.event_type, newEvent.title.trim(), newEvent.description);
    const isAnswer = ["whatsapp_recebido", "email_recebido"].includes(newEvent.event_type);
    if (isAnswer) await cancelFutureFollowUps(lead.id);
    await updateLead(lead.id, { last_contact_at: new Date().toISOString() });
    setNewEvent({ event_type: "nota", title: "", description: "" });
    toast({ title: isAnswer ? "Registrado — follow ups futuros cancelados" : "Registrado na timeline" });
    await loadAll();
    onSaved();
  };

  const addTask = async () => {
    if (!newTask.title.trim() || !newTask.due_at) {
      return toast({ title: "Informe título e data", variant: "destructive" });
    }
    await db.from("crm_lead_tasks").insert({
      lead_id: lead.id,
      title: newTask.title.trim(),
      description: newTask.description || null,
      due_at: new Date(newTask.due_at).toISOString(),
      priority: newTask.priority,
      task_type: newTask.task_type,
    });
    setNewTask({ title: "", due_at: "", priority: "media", task_type: "follow_up", description: "" });
    await loadAll();
    onSaved();
  };

  const toggleTask = async (t: CrmTask) => {
    await db
      .from("crm_lead_tasks")
      .update({ is_done: !t.is_done, done_at: t.is_done ? null : new Date().toISOString() })
      .eq("id", t.id);
    await loadAll();
    onSaved();
  };

  const removeTask = async (id: string) => {
    await db.from("crm_lead_tasks").delete().eq("id", id);
    await loadAll();
  };

  const runCadence = async () => {
    await createFollowUpSequence(lead.id);
    toast({ title: "Sequência de follow up criada" });
    await loadAll();
    onSaved();
  };

  const addLink = async () => {
    if (!newLink.listing_id) return;
    const l = listings.find((x) => x.id === newLink.listing_id);
    await db.from("crm_lead_listings").insert({
      lead_id: lead.id,
      listing_id: newLink.listing_id,
      listing_title: l?.title || null,
      relation: newLink.relation,
    });
    await logEvent(lead.id, "nota", `Imóvel ${newLink.relation}: ${l?.title || ""}`);
    setNewLink({ listing_id: "", relation: "enviado" });
    await loadAll();
  };

  const removeLink = async (id: string) => {
    await db.from("crm_lead_listings").delete().eq("id", id);
    await loadAll();
  };

  const uploadDoc = async (file: File, category: string) => {
    const path = `crm/${lead.id}/${Date.now()}-${file.name.replace(/\s+/g, "-")}`;
    const { error } = await supabase.storage.from("listing-images").upload(path, file);
    if (error) return toast({ title: "Erro no upload", description: error.message, variant: "destructive" });
    const { data } = supabase.storage.from("listing-images").getPublicUrl(path);
    await db.from("crm_lead_documents").insert({
      lead_id: lead.id,
      category,
      file_name: file.name,
      file_url: data.publicUrl,
    });
    await logEvent(lead.id, "arquivo", `Documento enviado: ${file.name}`);
    toast({ title: "Documento enviado" });
    await loadAll();
  };

  const removeDoc = async (id: string) => {
    await db.from("crm_lead_documents").delete().eq("id", id);
    await loadAll();
  };

  /** AI assistant: summary + next best step + ready-to-send message. */
  const askAi = async (mode: "resumo" | "proximo_passo" | "mensagem") => {
    setAiLoading(true);
    setAiText("");
    try {
      const { data, error } = await supabase.functions.invoke("crm-assistant", {
        body: {
          mode,
          lead: {
            name: form.client_name,
            stage: form.stage,
            classification: form.classification,
            score: form.score,
            goal: form.goal,
            client_type: form.client_type,
            interests: form.interests,
            price_range: form.price_range,
            regions: form.regions,
            bedrooms: form.bedrooms,
            garage: form.garage,
            source: form.source,
            campaign: form.campaign,
            notes: form.general_notes,
            last_contact_at: form.last_contact_at,
          },
          events: events.slice(0, 30).map((e) => ({ when: e.created_at, type: e.event_type, title: e.title, description: e.description })),
          listings: links.map((l) => ({ title: l.listing_title, relation: l.relation })),
        },
      });
      if (error) throw error;
      setAiText((data as { text?: string })?.text || "Sem resposta.");
    } catch (e) {
      setAiText("");
      toast({
        title: "Assistente indisponível",
        description: e instanceof Error ? e.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setAiLoading(false);
    }
  };

  const cls = CLASSIFICATIONS[form.classification] || CLASSIFICATIONS.morno;
  const wa = whatsappLink(form);
  const mail = emailLink(form);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex flex-wrap items-center gap-2">
            {form.client_name}
            <Badge variant="outline" className={`font-body text-[11px] ${cls.className}`}>
              {cls.label} · {form.score} pts
            </Badge>
            <span className="ml-auto flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => setComposerOpen(true)}>
                <MessageCircle className="h-4 w-4" /> Mensagens
              </Button>

              {wa && (
                <Button asChild size="sm" variant="outline">
                  <a href={wa} target="_blank" rel="noreferrer">
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </a>
                </Button>
              )}
              {mail && (
                <Button asChild size="sm" variant="outline">
                  <a href={mail}>
                    <Mail className="h-4 w-4" /> E-mail
                  </a>
                </Button>
              )}
              <Button size="sm" variant="gold" onClick={save} disabled={saving}>
                <Save className="h-4 w-4" /> Salvar
              </Button>
            </span>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="dados">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="dados">Dados</TabsTrigger>
            <TabsTrigger value="perfil">Perfil</TabsTrigger>
            <TabsTrigger value="timeline">Timeline ({events.length})</TabsTrigger>
            <TabsTrigger value="tarefas">Tarefas ({tasks.filter((t) => !t.is_done).length})</TabsTrigger>
            <TabsTrigger value="imoveis">Imóveis ({links.length})</TabsTrigger>
            <TabsTrigger value="documentos">Documentos ({docs.length})</TabsTrigger>
            <TabsTrigger value="ia">Assistente IA</TabsTrigger>
          </TabsList>

          {/* -------- Dados -------- */}
          <TabsContent value="dados" className="space-y-4 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Nome"><Input className="font-body" value={form.client_name} onChange={(e) => set({ client_name: e.target.value })} /></Field>
              <Field label="Telefone"><Input className="font-body" value={form.client_phone || ""} onChange={(e) => set({ client_phone: e.target.value })} /></Field>
              <Field label="WhatsApp"><Input className="font-body" value={form.client_whatsapp || ""} onChange={(e) => set({ client_whatsapp: e.target.value })} /></Field>
              <Field label="E-mail"><Input className="font-body" value={form.client_email || ""} onChange={(e) => set({ client_email: e.target.value })} /></Field>
              <Field label="CPF"><Input className="font-body" value={form.cpf || ""} onChange={(e) => set({ cpf: e.target.value })} /></Field>
              <Field label="RG"><Input className="font-body" value={form.rg || ""} onChange={(e) => set({ rg: e.target.value })} /></Field>
              <Field label="Nascimento"><Input type="date" className="font-body" value={form.birth_date || ""} onChange={(e) => set({ birth_date: e.target.value })} /></Field>
              <Field label="Estado civil"><Picker value={form.marital_status || ""} options={MARITAL_OPTIONS} placeholder="Selecione" onChange={(v) => set({ marital_status: v })} /></Field>
              <Field label="Profissão"><Input className="font-body" value={form.profession || ""} onChange={(e) => set({ profession: e.target.value })} /></Field>
              <Field label="Empresa"><Input className="font-body" value={form.company || ""} onChange={(e) => set({ company: e.target.value })} /></Field>
              <Field label="Renda (R$)"><Input type="number" className="font-body" value={form.income ?? ""} onChange={(e) => set({ income: e.target.value ? Number(e.target.value) : null })} /></Field>
              <Field label="Cidade"><Input className="font-body" value={form.city || ""} onChange={(e) => set({ city: e.target.value })} /></Field>
              <Field label="Estado"><Input className="font-body" value={form.state || ""} onChange={(e) => set({ state: e.target.value })} /></Field>
              <Field label="Endereço"><Input className="font-body" value={form.address || ""} onChange={(e) => set({ address: e.target.value })} /></Field>
              <Field label="Corretor responsável"><Input className="font-body" value={form.broker || ""} onChange={(e) => set({ broker: e.target.value })} /></Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Etapa do pipeline"><Picker value={form.stage} options={STAGES} placeholder="Etapa" onChange={(v) => set({ stage: v })} /></Field>
              <Field label="Valor do negócio (R$)"><Input type="number" className="font-body" value={form.deal_value ?? ""} onChange={(e) => set({ deal_value: e.target.value ? Number(e.target.value) : null })} /></Field>
              <Field label="Motivo de perda"><Input className="font-body" value={form.lost_reason || ""} onChange={(e) => set({ lost_reason: e.target.value })} /></Field>
            </div>

            <div className="rounded-md border border-border p-3 space-y-3">
              <p className="text-xs font-body font-semibold uppercase tracking-wide text-muted-foreground">Origem e campanha</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Origem"><Picker value={form.source || ""} options={SOURCES} placeholder="Selecione" onChange={(v) => set({ source: v })} /></Field>
                <Field label="Campanha"><Input className="font-body" value={form.campaign || ""} onChange={(e) => set({ campaign: e.target.value })} /></Field>
                <Field label="Grupo de anúncios"><Input className="font-body" value={form.ad_group || ""} onChange={(e) => set({ ad_group: e.target.value })} /></Field>
                <Field label="Anúncio"><Input className="font-body" value={form.ad_name || ""} onChange={(e) => set({ ad_name: e.target.value })} /></Field>
                <Field label="Palavra-chave"><Input className="font-body" value={form.keyword || ""} onChange={(e) => set({ keyword: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs font-body text-muted-foreground">
                <span>utm_source: {form.utm_source || "—"}</span>
                <span>utm_medium: {form.utm_medium || "—"}</span>
                <span>utm_campaign: {form.utm_campaign || "—"}</span>
                <span>utm_content: {form.utm_content || "—"}</span>
                <span>utm_term: {form.utm_term || "—"}</span>
                <span>Página de origem: {form.landing_page || "—"}</span>
              </div>
            </div>

            <Field label="Observações">
              <Textarea className="font-body" rows={4} value={form.general_notes || ""} onChange={(e) => set({ general_notes: e.target.value })} />
            </Field>
          </TabsContent>

          {/* -------- Perfil -------- */}
          <TabsContent value="perfil" className="space-y-4 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Objetivo"><Picker value={form.goal || ""} options={GOALS} placeholder="Selecione" onChange={(v) => set({ goal: v })} /></Field>
              <Field label="Tipo de cliente"><Picker value={form.client_type || ""} options={CLIENT_TYPES} placeholder="Selecione" onChange={(v) => set({ client_type: v })} /></Field>
              <Field label="Faixa de valor"><Picker value={form.price_range || ""} options={PRICE_RANGES} placeholder="Selecione" onChange={(v) => set({ price_range: v })} /></Field>
              <Field label="Quartos"><Picker value={form.bedrooms || ""} options={BEDROOM_OPTIONS} placeholder="Selecione" onChange={(v) => set({ bedrooms: v })} /></Field>
              <Field label="Garagem"><Picker value={form.garage || ""} options={GARAGE_OPTIONS} placeholder="Selecione" onChange={(v) => set({ garage: v })} /></Field>
            </div>
            <Field label="Interesse"><MultiCheck values={form.interests || []} options={INTERESTS} onChange={(v) => set({ interests: v })} /></Field>
            <Field label="Regiões"><MultiCheck values={form.regions || []} options={REGIONS} onChange={(v) => set({ regions: v })} /></Field>
          </TabsContent>

          {/* -------- Timeline -------- */}
          <TabsContent value="timeline" className="space-y-4 pt-4">
            <div className="rounded-md border border-border p-3 space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Picker value={newEvent.event_type} options={EVENT_TYPES} placeholder="Tipo" onChange={(v) => setNewEvent({ ...newEvent, event_type: v })} />
                <Input className="font-body md:col-span-2" placeholder="Título do registro" value={newEvent.title} onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })} />
              </div>
              <Textarea className="font-body" rows={2} placeholder="Detalhes" value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} />
              <Button size="sm" variant="gold" onClick={addEvent}><Plus className="h-4 w-4" /> Registrar</Button>
            </div>

            <ol className="relative border-l border-border pl-4 space-y-3">
              {events.map((e) => (
                <li key={e.id} className="relative">
                  <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-accent" />
                  <p className="font-body text-sm font-semibold text-foreground">{e.title}</p>
                  {e.description && <p className="font-body text-xs text-muted-foreground whitespace-pre-line">{e.description}</p>}
                  <p className="font-body text-[11px] text-muted-foreground mt-0.5">
                    {dateTimeBR(e.created_at)} · {EVENT_TYPES.find((t) => t.value === e.event_type)?.label || e.event_type} · {e.actor || "Sistema"}
                  </p>
                </li>
              ))}
              {events.length === 0 && <li className="font-body text-sm text-muted-foreground">Nenhum registro ainda.</li>}
            </ol>
          </TabsContent>

          {/* -------- Tarefas -------- */}
          <TabsContent value="tarefas" className="space-y-4 pt-4">
            <div className="rounded-md border border-border p-3 space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <Input className="font-body md:col-span-2" placeholder="Próxima ação" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} />
                <Input type="datetime-local" className="font-body" value={newTask.due_at} onChange={(e) => setNewTask({ ...newTask, due_at: e.target.value })} />
                <Picker value={newTask.priority} options={PRIORITIES} placeholder="Prioridade" onChange={(v) => setNewTask({ ...newTask, priority: v })} />
                <Picker value={newTask.task_type} options={TASK_TYPES} placeholder="Tipo" onChange={(v) => setNewTask({ ...newTask, task_type: v })} />
                <Input className="font-body md:col-span-3" placeholder="Descrição" value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="gold" onClick={addTask}><Plus className="h-4 w-4" /> Criar tarefa</Button>
                <Button size="sm" variant="outline" onClick={runCadence}>
                  <Clock className="h-4 w-4" /> Criar cadência automática (1, 3, 7, 15 e 30 dias)
                </Button>
              </div>
            </div>

            <ul className="space-y-2">
              {tasks.map((t) => {
                const late = !t.is_done && new Date(t.due_at) < new Date();
                return (
                  <li key={t.id} className={`flex items-center gap-2 rounded-md border p-2 ${late ? "border-destructive/60 bg-destructive/5" : "border-border"} ${t.is_done ? "opacity-50" : ""}`}>
                    <Button size="sm" variant="outline" onClick={() => toggleTask(t)}><Check className="h-3.5 w-3.5" /></Button>
                    <div className="min-w-0">
                      <p className={`font-body text-sm ${t.is_done ? "line-through" : "font-semibold"}`}>{t.title}</p>
                      <p className="font-body text-[11px] text-muted-foreground">
                        {dateTimeBR(t.due_at)} · {PRIORITIES.find((p) => p.value === t.priority)?.label} · {TASK_TYPES.find((x) => x.value === t.task_type)?.label}
                        {late && " · ATRASADA"}
                      </p>
                    </div>
                    <Button size="sm" variant="ghost" className="ml-auto text-destructive" onClick={() => removeTask(t.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </li>
                );
              })}
              {tasks.length === 0 && <li className="font-body text-sm text-muted-foreground">Nenhuma tarefa. Crie a próxima ação para não perder este lead.</li>}
            </ul>
          </TabsContent>

          {/* -------- Imóveis -------- */}
          <TabsContent value="imoveis" className="space-y-4 pt-4">
            <div className="rounded-md border border-border p-3 grid grid-cols-1 md:grid-cols-3 gap-2">
              <Select value={newLink.listing_id || undefined} onValueChange={(v) => setNewLink({ ...newLink, listing_id: v })}>
                <SelectTrigger className="font-body md:col-span-2"><SelectValue placeholder="Selecione o imóvel" /></SelectTrigger>
                <SelectContent>
                  {listings.map((l) => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}
                </SelectContent>
              </Select>
              <Picker value={newLink.relation} options={LISTING_RELATIONS} placeholder="Relação" onChange={(v) => setNewLink({ ...newLink, relation: v })} />
              <Button size="sm" variant="gold" onClick={addLink} className="md:col-span-3 w-fit"><Plus className="h-4 w-4" /> Vincular imóvel</Button>
            </div>

            <ul className="space-y-2">
              {links.map((l) => (
                <li key={l.id} className="flex items-center gap-2 rounded-md border border-border p-2">
                  <Home className="h-4 w-4 text-accent" />
                  <span className="font-body text-sm truncate">{l.listing_title}</span>
                  <Badge variant="outline" className="font-body text-[10px]">{LISTING_RELATIONS.find((r) => r.value === l.relation)?.label}</Badge>
                  <Button size="sm" variant="ghost" className="ml-auto text-destructive" onClick={() => removeLink(l.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </li>
              ))}
              {links.length === 0 && <li className="font-body text-sm text-muted-foreground">Nenhum imóvel vinculado.</li>}
            </ul>
          </TabsContent>

          {/* -------- Documentos -------- */}
          <TabsContent value="documentos" className="space-y-4 pt-4">
            <div className="rounded-md border border-border p-3 flex flex-wrap gap-2 items-end">
              {DOC_CATEGORIES.map((cat) => (
                <label key={cat} className="cursor-pointer">
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void uploadDoc(f, cat);
                      e.target.value = "";
                    }}
                  />
                  <span className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 font-body text-xs hover:border-accent">
                    <Upload className="h-3.5 w-3.5" /> {cat}
                  </span>
                </label>
              ))}
            </div>

            <ul className="space-y-2">
              {docs.map((d) => (
                <li key={d.id} className="flex items-center gap-2 rounded-md border border-border p-2">
                  <Badge variant="outline" className="font-body text-[10px]">{d.category}</Badge>
                  <a href={d.file_url} target="_blank" rel="noreferrer" className="font-body text-sm underline truncate">{d.file_name}</a>
                  <span className="font-body text-[11px] text-muted-foreground ml-auto">{dateTimeBR(d.created_at)}</span>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeDoc(d.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </li>
              ))}
              {docs.length === 0 && <li className="font-body text-sm text-muted-foreground">Nenhum documento enviado.</li>}
            </ul>
          </TabsContent>

          {/* -------- IA -------- */}
          <TabsContent value="ia" className="space-y-3 pt-4">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="gold" disabled={aiLoading} onClick={() => askAi("resumo")}><Sparkles className="h-4 w-4" /> Resumir histórico</Button>
              <Button size="sm" variant="outline" disabled={aiLoading} onClick={() => askAi("proximo_passo")}>Sugerir próximo passo</Button>
              <Button size="sm" variant="outline" disabled={aiLoading} onClick={() => askAi("mensagem")}>Gerar mensagem de WhatsApp</Button>
            </div>
            <Textarea
              className="font-body min-h-[240px]"
              value={aiLoading ? "Analisando o histórico do cliente…" : aiText}
              onChange={(e) => setAiText(e.target.value)}
              placeholder="O assistente resume o histórico, sugere o próximo passo ideal e escreve a mensagem pronta para enviar."
            />
          </TabsContent>
        </Tabs>

        {composerOpen && (
          <CrmMessageComposer
            lead={form}
            open={composerOpen}
            onClose={() => setComposerOpen(false)}
            onSent={async () => {
              await loadAll();
              onSaved();
            }}
          />
        )}
      </DialogContent>
    </Dialog>

  );
};

export default CrmLeadSheet;
